import { Fragment, useEffect, useMemo, useState, type ClipboardEvent } from 'react';
import { alterarFurosSpt, lerNumero, metro, type CampanhaSpt, type FuroDaCampanha } from '../../api/spt';
import { ErroApi } from '../../api';
import { plural } from '../../formato';
import { Area, Dialogo, ErroDaApi } from '../../ui';
import { FormularioDaJanela } from '../cptu/FormularioDaJanela';
import { ReferenciaNivelDaOs } from '../ReferenciaNivelDaOs';

/**
 * A Edição rápida dos furos SPT — onda 366, no molde da Edição rápida das
 * verticais do CPTu (333/334): «Assim como no CPTu, faça uma edição rápida dos
 * SPTs, onde seja possível editar as coordenadas, NA, Cota, etc.» (Humberto,
 * 02/11/2026). Cada furo numa linha — as datas e horas, o sondador, a cota, o
 * UTM, a profundidade final, o revestimento e a leitura do NA que vai ao
 * boletim; o que mudou fica marcado; o bloco copiado do Excel se distribui
 * pelas linhas de baixo e pelas colunas à direita; «Gravar os N furos» pede o
 * motivo UMA vez e grava o lote numa transação (`PATCH
 * /spt/campanhas/:id/furos`, todos ou nenhum). Embaixo, a RN da OS — que se
 * lança daqui, com a permissão do SPT («Não consigo lançar o RN»).
 */

export type ColunaSpt = 'inicio' | 'horaInicio' | 'termino' | 'horaTermino' | 'sondador' | 'cota' | 'e' | 'n' | 'fuso'
  | 'fim' | 'rev' | 'na' | 'naData' | 'naHora' | 'naAberto';
export type RascunhoDoFuro = Record<ColunaSpt, string>;

export const COLUNAS_SPT: readonly ColunaSpt[] = ['inicio', 'horaInicio', 'termino', 'horaTermino', 'sondador', 'cota', 'e', 'n',
  'fuso', 'fim', 'rev', 'na', 'naData', 'naHora', 'naAberto'];
export const NOME_DA_COLUNA_SPT: Record<ColunaSpt, string> = {
  inicio: 'Início', horaInicio: 'Hora de início', termino: 'Término', horaTermino: 'Hora de término', sondador: 'Sondador',
  cota: 'Cota (m)', e: 'UTM E (m)', n: 'UTM N (m)', fuso: 'Fuso', fim: 'Profundidade final (m)', rev: 'Revestimento Ø (mm)',
  na: 'NA (m)', naData: 'Data do NA', naHora: 'Hora do NA', naAberto: 'Aberto até (m)',
};
const TIPO: Partial<Record<ColunaSpt, 'date' | 'time'>> = {
  inicio: 'date', termino: 'date', naData: 'date', horaInicio: 'time', horaTermino: 'time', naHora: 'time',
};
const NUMERICAS: ColunaSpt[] = ['cota', 'e', 'n', 'fuso', 'fim', 'rev', 'naAberto'];

const dia = (x: string | null | undefined) => (x ? String(x).slice(0, 10) : '');
const hora = (x: string | null | undefined) => (x ? String(x).slice(0, 5) : '');
const num = (x: number | null | undefined, casas: number) => (x == null ? '' : metro(x, casas));

export function rascunhoDoFuro(f: FuroDaCampanha): RascunhoDoFuro {
  const na = f.na_boletim ?? null;
  return {
    inicio: dia(f.inicio), horaInicio: hora(f.hora_inicio), termino: dia(f.termino), horaTermino: hora(f.hora_termino),
    sondador: f.sondador ?? '', cota: num(f.cota, 3), e: num(f.utm_e, 2), n: num(f.utm_n, 2),
    fuso: f.utm_fuso == null ? '' : String(f.utm_fuso), fim: num(f.profundidade_final, 2), rev: num(f.revestimento_diametro, 1),
    na: na ? (na.seco ? 'seco' : num(na.profundidade, 2)) : '', naData: dia(na?.data), naHora: hora(na?.hora),
    naAberto: num(na?.aberto_ate, 2),
  };
}

/** O que está errado na linha, coluna a coluna. */
export function errosDoFuro(r: RascunhoDoFuro): Partial<Record<ColunaSpt, string>> {
  const e: Partial<Record<ColunaSpt, string>> = {};
  for (const k of NUMERICAS) if (Number.isNaN(lerNumero(r[k]) as number)) e[k] = 'não é número';
  const seco = /^seco$/i.test(r.na.trim());
  if (r.na.trim() && !seco && Number.isNaN(lerNumero(r.na) as number)) e.na = 'número, ou «seco»';
  if (r.na.trim() && !r.naData) e.naData = 'a data da leitura';
  if (!r.na.trim() && (r.naData || r.naHora || r.naAberto.trim())) e.na = 'o NA (m) ou «seco»';
  const fuso = lerNumero(r.fuso);
  if (fuso != null && !Number.isNaN(fuso) && (fuso < 18 || fuso > 25 || !Number.isInteger(fuso))) e.fuso = 'de 18 a 25';
  if ((r.e.trim() || r.n.trim()) && !(r.e.trim() && r.n.trim())) e[r.e.trim() ? 'n' : 'e'] = 'E e N vêm juntos';
  return e;
}

/** O corpo do lote para o furo (só o que mudou), as colunas mudadas e as frases da janela. */
export function mudancasDoFuro(f: FuroDaCampanha, r: RascunhoDoFuro) {
  const o = rascunhoDoFuro(f);
  const campos = new Set(COLUNAS_SPT.filter((k) => o[k].trim() !== r[k].trim()));
  if (!campos.size) return null;
  const corpo: Record<string, unknown> = { id: f.id };
  const frases: string[] = [];
  const n = (k: ColunaSpt) => lerNumero(r[k]);
  const diga = (k: ColunaSpt) => frases.push(`${NOME_DA_COLUNA_SPT[k].toLowerCase()} ${o[k] || '—'} → ${r[k] || '—'}`);
  if (campos.has('inicio')) { corpo.inicio = r.inicio || null; diga('inicio'); }
  if (campos.has('horaInicio')) { corpo.horaInicio = r.horaInicio || null; diga('horaInicio'); }
  if (campos.has('termino')) { corpo.termino = r.termino || null; diga('termino'); }
  if (campos.has('horaTermino')) { corpo.horaTermino = r.horaTermino || null; diga('horaTermino'); }
  if (campos.has('sondador')) { corpo.sondador = r.sondador.trim() || null; diga('sondador'); }
  if (campos.has('cota')) { corpo.cota = n('cota'); diga('cota'); }
  if (campos.has('e') || campos.has('n') || campos.has('fuso')) {
    corpo.utmE = n('e'); corpo.utmN = n('n'); corpo.utmFuso = n('fuso') ?? 22;
    (['e', 'n', 'fuso'] as ColunaSpt[]).filter((k) => campos.has(k)).forEach(diga);
  }
  if (campos.has('fim')) { corpo.profundidadeFinal = n('fim'); diga('fim'); }
  if (campos.has('rev')) { corpo.revestimentoDiametro = n('rev'); diga('rev'); }
  if (campos.has('na') || campos.has('naData') || campos.has('naHora') || campos.has('naAberto')) {
    const seco = /^seco$/i.test(r.na.trim());
    corpo.naBoletim = !r.na.trim() ? null : {
      data: r.naData, hora: r.naHora || null, seco, profundidade: seco ? null : n('na'), abertoAte: n('naAberto'),
    };
    frases.push(!r.na.trim() ? 'tira o NA do boletim'
      : `NA do boletim ${seco ? 'seco' : `${r.na} m`} em ${r.naData.split('-').reverse().join('/')}${r.naHora ? ` às ${r.naHora}` : ''}`
        + `${r.naAberto.trim() ? `, aberto até ${r.naAberto} m` : ''}`);
  }
  return { corpo, campos, frases };
}

/** O bloco do Excel (tabulação e linhas); a célula só (sem tabulação nem linha) cola como sempre. */
function blocoColado(texto: string): string[][] | null {
  const t = texto.replace(/\r\n?/g, '\n').replace(/\n+$/, '');
  if (!t.includes('\t') && !t.includes('\n')) return null;
  return t.split('\n').map((l) => l.split('\t'));
}

export function EdicaoRapidaDoSpt({ c, podeEditar, aoGravado }: {
  c: CampanhaSpt; podeEditar: boolean; aoGravado(): Promise<void>;
}) {
  const [rascunhos, setRascunhos] = useState<Record<string, RascunhoDoFuro>>({});
  const [janela, setJanela] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [errosDoServidor, setErrosDoServidor] = useState<Record<string, string>>({});
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const ed = c.editavel && podeEditar;

  const linhas = useMemo(() => c.furos.map((f) => {
    const r = rascunhos[f.id] ?? rascunhoDoFuro(f);
    const erros = errosDoFuro(r);
    const temErro = Object.keys(erros).length > 0;
    const mudancas = temErro ? null : mudancasDoFuro(f, r);
    return { f, r, erros, temErro, mudancas };
  }), [c.furos, rascunhos]);
  const comErro = linhas.filter((l) => l.temErro).length;
  const mudadas = linhas.filter((l) => l.mudancas);
  const sujo = comErro > 0 || mudadas.length > 0;

  useEffect(() => {
    if (!sujo) return undefined;
    const aoSair = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', aoSair);
    return () => window.removeEventListener('beforeunload', aoSair);
  }, [sujo]);

  const mudar = (id: string, parte: Partial<RascunhoDoFuro>) => {
    const f = c.furos.find((x) => x.id === id)!;
    setRascunhos((antes) => ({ ...antes, [id]: { ...(antes[id] ?? rascunhoDoFuro(f)), ...parte } }));
    setErrosDoServidor((antes) => { const x = { ...antes }; delete x[id]; return x; });
  };

  const aoColar = (i: number, col: ColunaSpt) => (e: ClipboardEvent<HTMLInputElement>) => {
    const bloco = blocoColado(e.clipboardData.getData('text/plain'));
    if (!bloco) return;
    e.preventDefault();
    const j0 = COLUNAS_SPT.indexOf(col);
    const novos = { ...rascunhos };
    let linhasDeFora = 0;
    bloco.forEach((cels, d) => {
      const f = c.furos[i + d];
      if (!f) { linhasDeFora += 1; return; }
      const r = { ...(novos[f.id] ?? rascunhoDoFuro(f)) };
      cels.forEach((v, k) => { const coluna = COLUNAS_SPT[j0 + k]; if (coluna) r[coluna] = v.trim(); });
      novos[f.id] = r;
    });
    setRascunhos(novos);
    setAviso(`Colado da planilha: ${plural(bloco.length, 'linha', 'linhas')} a partir de ${c.furos[i].codigo}, `
      + `${NOME_DA_COLUNA_SPT[col].toLowerCase()}.${linhasDeFora ? ` Ficou de fora: ${plural(linhasDeFora, 'linha', 'linhas')} depois do último furo.` : ''}`);
  };

  const motivoDoGravar = !ed ? null : comErro
    ? `Corrija ${plural(comErro, 'furo', 'furos')} antes de gravar.`
    : mudadas.length === 0 ? 'Nada mudou ainda.' : null;
  const rotuloDoGravar = mudadas.length === 1 ? 'Gravar 1 furo' : mudadas.length ? `Gravar os ${mudadas.length} furos` : 'Gravar';
  const curto = motivo.trim().length < 5;

  const gravar = async () => {
    if (motivoDoGravar || curto || ocupado) return;
    setOcupado(true); setErro(null);
    const lote = mudadas.map((l) => l.mudancas!.corpo);
    try {
      await alterarFurosSpt(c.id, lote, motivo.trim());
      setJanela(false); setMotivo(''); setOcupado(false); setRascunhos({}); setAviso(null);
      await aoGravado();
    } catch (e) {
      setOcupado(false);
      const porLinha: Record<string, string> = {};
      if (e instanceof ErroApi) {
        for (const x of e.campos) {
          const m = /^furos\.(\d+)(?:\.|$)/.exec(x.campo);
          const id = m ? (lote[Number(m[1])]?.id as string | undefined) : undefined;
          if (id) porLinha[id] = porLinha[id] ? `${porLinha[id]}; ${x.erro}` : x.erro;
        }
      }
      setErrosDoServidor(porLinha);
      if (Object.keys(porLinha).length) setJanela(false);
      setErro(e);
    }
  };

  return (
    <>
      <section className="cartao cptu-er spt-er" aria-label="Edição rápida dos furos">
        <div className="cptu-er-barra">
          <p className="cptu-er-dica">
            {plural(c.furos.length, 'furo', 'furos')} · UTM em metros (SIRGAS 2000) · o NA é a leitura que vai ao boletim
            (em metros, ou «seco») · cole um bloco da planilha numa célula e ele se distribui pelas linhas de baixo e pelas
            colunas à direita.
          </p>
          {ed && (
            <div className="cptu-er-acoes spt-er-acoes">
              {sujo && <button type="button" className="botao secundario" onClick={() => { setRascunhos({}); setAviso(null); }}>Desfazer</button>}
              <button type="button" className="botao" aria-disabled={motivoDoGravar ? true : undefined}
                aria-describedby={motivoDoGravar ? 'spt-er-motivo-gravar' : undefined}
                onClick={() => { if (!motivoDoGravar) { setErro(null); setJanela(true); } }}>
                {rotuloDoGravar}
              </button>
              {motivoDoGravar && <small id="spt-er-motivo-gravar" className="cptu-motivo-bloqueio">{motivoDoGravar}</small>}
            </div>
          )}
        </div>
        {!c.editavel && <p className="sub">A campanha não está em rascunho nem devolvida: a edição rápida só mostra.</p>}
        {aviso && <p className="cptu-er-colagem" role="status">{aviso}</p>}
        {erro && !janela ? <ErroDaApi erro={erro} /> : null}
        {c.furos.length === 0 ? <p className="sub">Nenhum furo ainda.</p> : (
          <div className="rola">
            <table className="tf-tabela cptu-er-grade spt-er-grade" aria-label="Edição rápida dos furos">
              <thead>
                <tr>
                  <th scope="col">Furo</th>
                  {COLUNAS_SPT.map((k) => <th key={k} scope="col">{NOME_DA_COLUNA_SPT[k]}</th>)}
                </tr>
              </thead>
              <tbody>
                {linhas.map(({ f, r, erros, mudancas, temErro }, i) => {
                  const doServidor = errosDoServidor[f.id];
                  return (
                    <Fragment key={f.id}>
                      <tr className={['cptu-er-linha spt-er-linha', mudancas ? 'cptu-er-mudou' : '', temErro || doServidor ? 'cptu-er-com-erro' : '']
                        .filter(Boolean).join(' ')}>
                        <th scope="row">
                          <span className="mono">{f.codigo}</span>
                          {mudancas && <span className="selo atencao cptu-er-selo">Alterado</span>}
                          {temErro && <span className="selo critico cptu-er-selo">Com erro</span>}
                        </th>
                        {COLUNAS_SPT.map((k) => {
                          const rotulo = `${NOME_DA_COLUNA_SPT[k]} do furo ${f.codigo}`;
                          return (
                            <td key={k} className={mudancas?.campos.has(k) ? 'cptu-er-celula mudou' : 'cptu-er-celula'}>
                              <input type={TIPO[k] ?? 'text'} disabled={!ed}
                                inputMode={TIPO[k] || k === 'sondador' || k === 'na' ? undefined : 'decimal'}
                                className={[k === 'sondador' ? '' : 'mono', k === 'fuso' || k === 'rev' ? 'spt-er-curto' : ''].filter(Boolean).join(' ') || undefined}
                                value={r[k]} data-coluna={k} aria-label={rotulo}
                                aria-invalid={erros[k] ? true : undefined}
                                aria-describedby={erros[k] ? `spt-er-${f.id}-${k}` : undefined}
                                placeholder={k === 'na' ? 'm ou seco' : k === 'rev' ? '63,5' : k === 'fuso' ? '22' : undefined}
                                onChange={(e) => mudar(f.id, { [k]: e.target.value })}
                                onPaste={TIPO[k] ? undefined : aoColar(i, k)} />
                              {erros[k] && <span id={`spt-er-${f.id}-${k}`} className="erro">{erros[k]}</span>}
                            </td>
                          );
                        })}
                      </tr>
                      {doServidor && (
                        <tr className="cptu-er-erro-do-servidor">
                          <td colSpan={COLUNAS_SPT.length + 1}><span className="erro" role="alert">{f.codigo}: {doServidor}</span></td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="spt-er-rn">
        <ReferenciaNivelDaOs osId={c.os_id} campanhaSpt={c.id} podeEditar={ed} aoGravar={() => { void aoGravado(); }} />
      </div>

      {janela && (
        <Dialogo aberto titulo="Gravar a Edição rápida" rotulo={`Furos da campanha ${c.numero}`} aoFechar={() => setJanela(false)}>
          <FormularioDaJanela aoEnviar={() => { void gravar(); }}>
            <ErroDaApi erro={erro} />
            <p className="sub">
              {plural(mudadas.length, 'furo muda', 'furos mudam')}, todos ou nenhum. Os golpes e as camadas não mudam; a cota e a
              coordenada vão também aos pontos da OS.
            </p>
            <ul className="cptu-mudancas cptu-er-lista">
              {mudadas.map((l) => <li key={l.f.id}><b className="mono">{l.f.codigo}</b>: {l.mudancas!.frases.join('; ')}</li>)}
            </ul>
            <Area rotulo="Motivo da alteração" rows={2} value={motivo} maxLength={2000}
              dica="Vai para a trilha de cada furo." onChange={(e) => setMotivo(e.target.value)} />
            {curto && <p className="cptu-falta" role="status">Diga o motivo da alteração (pelo menos 5 caracteres).</p>}
            <div className="rodape-dialogo">
              <button type="submit" className="botao" disabled={curto || ocupado}>{ocupado ? 'Gravando…' : rotuloDoGravar}</button>
              <button type="button" className="botao secundario" onClick={() => setJanela(false)}>Cancelar</button>
            </div>
          </FormularioDaJanela>
        </Dialogo>
      )}
    </>
  );
}
